import { putJson } from '../services/apiClient';

export const extractFirstImageFromContent = (content) => {
  if (!content || typeof content !== 'string') return null;
  const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/i;
  const match = content.match(imgRegex);
  return match?.[1] ?? null;
};

export const processPostData = (post) => {
  if (!post.featured_image && !post.first_image_url && post.content) {
    const extractedImage = extractFirstImageFromContent(post.content);
    if (extractedImage) {
      return { ...post, featured_image: extractedImage };
    }
  }
  return post;
};

export const processPostsData = (posts) => {
  if (!Array.isArray(posts)) return posts;
  return posts.map(processPostData);
};

export async function fetchPostDetail(postId) {
  const response = await fetch(`/api/post_detail?id=${postId}`, {
    credentials: 'include'
  });
  const data = await response.json();

  if (!data.success || !data.post) {
    throw new Error(`포스트 정보를 불러올 수 없습니다 (ID: ${postId})`);
  }

  return data.post;
}

export async function fetchPostRelationships(postId) {
  const response = await fetch(`/api/relation?id=${postId}`, {
    credentials: 'include'
  });
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || '관계 정보 조회 실패');
  }

  return data.relationships || { previous: [], next: [], parents: [], children: [] };
}

export async function updatePostRelationsOnly(postId, relationships) {
  const data = await putJson('/api/post/relations', {
    post_id: String(postId),
    relationships,
  });

  if (!data.success) {
    throw new Error(data.error || data.message || '관계 정보 업데이트 실패');
  }

  return data;
}

export async function updatePostRelationships(postId, post, relationships, defaultCategory = null) {
  const data = await putJson('/api/post', {
    postId: String(postId),
    title: post.title,
    content: post.content,
    abstract: post.abstract,
    category: parseInt(post.category) || defaultCategory || 1,
    tags: post.tags || '',
    postType: 'mypost',
    relationships,
  });

  if (!data.success) {
    throw new Error(data.error || data.message || '포스트 업데이트 실패');
  }

  return data;
}

export async function updateParentChildren(parentId, childId, action = 'add', defaultCategory = null) {
  const parentRelationships = await fetchPostRelationships(parentId);
  const currentChildren = Array.isArray(parentRelationships.children) ? parentRelationships.children : [];
  const childIdStr = String(childId);
  const childIdNum = parseInt(childId);

  let updatedChildren;
  if (action === 'add') {
    if (!currentChildren.includes(childIdStr) && !currentChildren.includes(childIdNum)) {
      updatedChildren = [...currentChildren, childIdStr];
    } else {
      return;
    }
  } else if (action === 'remove') {
    updatedChildren = currentChildren.filter(id => String(id) !== childIdStr && id !== childIdNum);
  } else {
    throw new Error(`Invalid action: ${action}. Use 'add' or 'remove'.`);
  }

  await updatePostRelationsOnly(parentId, {
    previous: Array.isArray(parentRelationships.previous) ? parentRelationships.previous : [],
    next: Array.isArray(parentRelationships.next) ? parentRelationships.next : [],
    parents: Array.isArray(parentRelationships.parents) ? parentRelationships.parents : [],
    children: updatedChildren,
  });
}

export async function movePostInHierarchy(movingPostId, targetPostId, relationType) {
  const movingRelations = await fetchPostRelationships(movingPostId);
  const oldParents = movingRelations.parents || [];

  let newParents = [];
  if (relationType === 'child') {
    newParents = targetPostId ? [String(targetPostId)] : [];
  } else if (relationType === 'sibling') {
    const targetRelations = await fetchPostRelationships(targetPostId);
    newParents = targetRelations.parents || [];
  }

  await updatePostRelationsOnly(movingPostId, { ...movingRelations, parents: newParents });

  for (const oldParentId of oldParents) {
    try {
      const oldParentRelations = await fetchPostRelationships(oldParentId);
      const updatedChildren = (oldParentRelations.children || []).filter(
        id => String(id) !== String(movingPostId)
      );
      await updatePostRelationsOnly(oldParentId, { ...oldParentRelations, children: updatedChildren });
    } catch (err) {
      console.error(`[movePostInHierarchy] 원래 부모(${oldParentId}) 업데이트 실패:`, err);
    }
  }

  if (relationType === 'child' && targetPostId) {
    const targetRelations = await fetchPostRelationships(targetPostId);
    const currentChildren = targetRelations.children || [];
    if (!currentChildren.some(id => String(id) === String(movingPostId))) {
      await updatePostRelationsOnly(targetPostId, {
        ...targetRelations,
        children: [...currentChildren, String(movingPostId)],
      });
    }
  }
}
